import type { RoleFragment } from "@/__generated__/graphql";
import { Select } from "@/components/molecules/Select";
import { Typography } from "@/components/ui/typography";

import { describePermissions } from "./Roles.tools";

/**
 * Picking one of a business's roles, each named with what it allows. What
 * someone holding it may really do where a grant holds them to less is the
 * server's to say, beside it.
 */
export function RolesSelect({
  roles,
  value,
  onChange,
  disabled = false,
  "data-testid": dataTestid,
}: {
  roles: RoleFragment[];
  /** The role picked: its identifier, or none yet. */
  value: string | null;
  onChange: (role: RoleFragment) => void;
  disabled?: boolean;
  "data-testid"?: string;
}) {
  const picked = roles.find((role) => role.identifier === value) ?? null;
  // Not the reader's to change: what it is, as words.
  if (disabled) {
    return (
      <Typography
        level="body-sm"
        data-testid={dataTestid}
        className="truncate text-muted-foreground shrink-0"
      >
        {picked
          ? `${picked.roleName} · ${describePermissions(picked.permissions)}`
          : null}
      </Typography>
    );
  }
  return (
    <div className="w-55 shrink-0" data-testid={dataTestid}>
      <Select<RoleFragment>
        value={picked}
        onChange={onChange}
        data={roles}
        getOptionKey={(role) => role.identifier}
        size="sm"
        popperMinWidth="260px"
        placement="bottom-end"
      >
        <Select.Trigger placeholder="Pick a role">
          {picked ? picked.roleName : null}
        </Select.Trigger>
        <Select.Options<RoleFragment>>
          {(role) => (
            <Select.Option value={role}>
              <div className="min-w-0 pt-0.5 pb-0.5">
                <Typography level="body-sm">{role.roleName}</Typography>
                <Typography
                  level="body-xs"
                  className="text-muted-foreground/80"
                >
                  {describePermissions(role.permissions)}
                </Typography>
              </div>
            </Select.Option>
          )}
        </Select.Options>
      </Select>
    </div>
  );
}
